import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Dialog } from 'radix-ui'
import { X } from 'lucide-react'
import { cn } from '@/lib/cn'
import { ConfirmDialog } from './ConfirmDialog'
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges'

/**
 * Modal on Radix Dialog. Radix supplies what the hand-rolled version lacked (spec
 * §3.13.5): a real focus trap, focus return to the trigger, Escape and scroll lock, and
 * a portal into document.body so no table's overflow can clip it. Same props as before.
 */
interface Props {
  open: boolean
  title?: ReactNode
  description?: ReactNode
  onClose: () => void
  size?: 'sm' | 'md' | 'lg' | 'xl'
  footer?: ReactNode
  children: ReactNode
  /**
   * Ask before Escape / the X / a backdrop click throws away typed input (spec 20 §3.13.5,
   * E-UX). On by default; explicit Cancel buttons and a successful save close as before.
   */
  confirmDiscard?: boolean
}

const SZ: Record<NonNullable<Props['size']>, string> = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
}

export function Modal({ open, title, description, onClose, size = 'md', footer, children, confirmDiscard = true }: Props) {
  // Any typing / choosing inside the dialog since it opened marks it as having unsaved input.
  const edited = useRef(false)
  const [asking, setAsking] = useState(false)
  // Mirrors `edited` as state so the app-wide unsaved-changes guard also covers the back button,
  // in-app links and tab close while a dirty dialog is open (not only Escape / X / backdrop).
  const [dirty, setDirty] = useState(false)
  useEffect(() => { if (open) { edited.current = false; setDirty(false) } }, [open])
  useUnsavedChanges(open && confirmDiscard && dirty)
  const markEdited = (e: { target: EventTarget }) => {
    const t = e.target as HTMLElement
    if (t && ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName) && (t as HTMLInputElement).type !== 'search') { edited.current = true; if (!dirty) setDirty(true) }
  }
  const requestClose = () => {
    if (confirmDiscard && edited.current) setAsking(true)
    else onClose()
  }
  return (
    <>
    <Dialog.Root open={open} onOpenChange={(o) => { if (!o) requestClose() }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[60] bg-overlay/40 backdrop-blur-[2px] data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <Dialog.Content
          onInputCapture={markEdited}
          onChangeCapture={markEdited}
          className={cn(
            'fixed left-1/2 top-[8vh] z-[70] flex max-h-[84vh] w-[calc(100vw-2rem)] -translate-x-1/2 flex-col',
            'rounded-overlay border border-border bg-card shadow-e4 focus:outline-none',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-[0.98] data-[state=open]:slide-in-from-top-2',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-[0.98]',
            'duration-slow ease-emphasized',
            SZ[size],
          )}
          {...(description ? {} : { 'aria-describedby': undefined })}
        >
          <div className="flex items-start justify-between gap-4 px-6 pb-4 pt-5">
            <div className="min-w-0">
              <Dialog.Title className="text-base font-semibold leading-6 text-foreground">{title}</Dialog.Title>
              {description && (
                <Dialog.Description className="mt-1 text-sm text-muted-foreground">{description}</Dialog.Description>
              )}
            </div>
            <Dialog.Close
              className="-mr-1.5 -mt-0.5 grid size-8 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors duration-fast hover:bg-surface-hover hover:text-foreground"
              aria-label="Close"
            >
              <X className="size-4" />
            </Dialog.Close>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-5">{children}</div>
          {footer && (
            <div className="flex items-center justify-end gap-2 rounded-b-overlay border-t border-border bg-background/60 px-6 py-3.5">
              {footer}
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
    <ConfirmDialog
      open={asking}
      title="Discard your changes?"
      message="What you entered in this dialog has not been saved."
      confirmLabel="Discard"
      cancelLabel="Keep editing"
      destructive
      onCancel={() => setAsking(false)}
      onConfirm={() => { setAsking(false); edited.current = false; setDirty(false); onClose() }}
    />
    </>
  )
}
